import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { first, firstValueFrom } from 'rxjs';
import { AlertService } from 'src/app/services/common/alert.service';
import { StorageKey, StorageService } from 'src/app/services/common/storage.service';
import { RoleManagementService } from 'src/app/services/role-management.service';
import { SubscribePlanRequest, SubscriptionService } from 'src/app/services/subscription.service';
import { UserInfoService } from 'src/app/services/user-info.service';
import { UserService } from 'src/app/services/user.service';
import { CompanyService } from 'src/app/services/vcard-company.service';
import { VcardUserService } from 'src/app/services/vcard-user.service';

interface Plan {
  id: string | number;
  title?: string;
  name?: string;
  originalPrice?: string;
  currentPrice?: string;
  price?: string;
  introPrice?: string;
  yearlyPrice?: string;
  savings?: string;
  period?: string;
  subtext?: string;
  features: string[];
  btnText: string;
}

declare global {
  interface Window {
    Razorpay: any;
    Stripe: any;
  }
}
@Component({
  selector: 'app-subscription-plan',
  templateUrl: './subscription-plan.component.html',
  styleUrls: ['./subscription-plan.component.css']
})
export class SubscriptionPlanComponent implements OnInit {
  @ViewChild('planPriceModal') planPriceModal!: TemplateRef<any>;
  @ViewChild('contactModal') contactModal!: TemplateRef<any>;
  @ViewChild('stripePaymentModal') stripePaymentModal!: TemplateRef<any>;
  activePlanId: string | number = ''; // Tracks Section 1
  selectedPlanId: string | number = "";      // Tracks Section 2
  detectedCountry: string | null = null;
  planAmount: number | null = null;
  planAmountYearly: number | null = null;
  user: number = 0;
  userOptions: number[] = [];
  soloPlan: any = null;
  teamPlans: any[] = [];
  selectedTeamPlan: any = null;
  smartSitePlans: any[] = [];
  vcardPlansForView: any[] = [];
  isPlansReady: boolean = false;
  selectedPlanForSubscription: any = null;
  selectedMonthlyAmount: number = 0;
  selectedAnnualAmount: number = 0;
  selectedBilling: 'monthly' | 'annual' = 'monthly';
  isProcessingSubscription: boolean = false;
  priceModalRef: MatDialogRef<any> | null = null;
  stripeModalRef: MatDialogRef<any> | null = null;
  stripeErrorMessage: string = '';
  private stripe: any = null;
  private stripeElements: any = null;
  private stripePaymentElement: any = null;
  private stripePaymentIntentId: string = '';
  private pendingStripeContext: { userId: string; currentUser: any } | null = null;
  enterprisePlan: any = {
    id: 'enterprise',
    title: 'ENTERPRISE',
    subtext: 'Everything in Team, plus enterprise-grade power.',
    features: ['CRM integration', 'API access', 'Custom workflows', 'Advanced permissions'],
    btnText: 'Contact Sales'
  };
  constructor(public dialogRef: MatDialogRef<SubscriptionPlanComponent>,
    private dialog: MatDialog,
    private router: Router,
    private storageService: StorageService,
    private roleManagementService: RoleManagementService,
    private userInfoService: UserInfoService,
    private userService: UserService,
    private alertService: AlertService,
    private subscriptionService: SubscriptionService,
    private vcardUserService: VcardUserService,
    private vcardCompService: CompanyService
  ) { }



  async ngOnInit() {
    this.detectedCountry = await this.tryIpGeolocation();
    await this.getSubscriptionPlans();
    this.isPlansReady = true;
    setTimeout(() => {
      this.openPendingSubscriptionPlan();
    });
  }
  selectUser(num: number) {
    this.user = num;
    this.checkForTeam();
  }

  checkForTeam() {
    if (!this.teamPlans.length) {
      this.selectedTeamPlan = null;
      this.planAmount = null;
      this.planAmountYearly = null;
      this.buildVcardPlansForView();
      return;
    }

    this.selectedTeamPlan = this.teamPlans.find((x: any) => x.vcardCount === this.user) || this.teamPlans[0];
    this.buildVcardPlansForView();

    const priceModel = this.detectedCountry === 'IN'
      ? this.selectedTeamPlan?.price?.india
      : this.selectedTeamPlan?.price?.default;

    this.planAmount = priceModel?.current ?? null;
    this.planAmountYearly = priceModel?.yearlyPrice ?? null;
  }

  getPriceModel(plan: any): any {
    return this.detectedCountry === 'IN' ? plan?.price?.india : plan?.price?.default;
  }

  private buildVcardPlansForView(): void {
    const list: any[] = [];
    if (this.soloPlan) list.push(this.soloPlan);
    if (this.selectedTeamPlan) {
      list.push({
        ...this.selectedTeamPlan,
        title: 'TEAM',
        id: this.selectedTeamPlan.id || 'team',
        subtext: 'Select QR pack'
      });
    }
    list.push(this.enterprisePlan);
    this.vcardPlansForView = list;
  }

  formatPlanPrice(plan: any, key: 'current' | 'original' | 'yearlyPrice'): string {
    return this.formatPriceLabel(this.getPriceModel(plan)?.[key], this.getPriceModel(plan));
  }

  formatTeamPrice(amount: number | null): string {
    return this.formatPriceLabel(amount, this.getPriceModel(this.selectedTeamPlan));
  }

  formatSelectedPlanPrice(amount: number): string {
    return this.formatPriceLabel(amount, this.getPriceModel(this.selectedPlanForSubscription));
  }

  isFreeAmount(amount: any): boolean {
    return Number(amount ?? 0) === 0;
  }

  formatPriceLabel(amount: any, priceModel: any): string {
    return this.isFreeAmount(amount) ? 'Free Trial' : this.formatAmount(amount, priceModel);
  }

  private formatAmount(amount: any, priceModel: any): string {
    const value = Number(amount ?? 0);
    const symbol = priceModel?.currency || this.getCurrencySymbolFallback();
    return `${symbol}${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  }

  private getCurrencySymbolFallback(): string {
    return this.detectedCountry === 'IN' ? '₹' : '$';
  }

  private getApiCurrencySymbol(plan: any): string | null {
    const value = plan?.currencySymbol || plan?.currency_symbol || plan?.currency || null;
    return this.normalizeCurrencySymbol(value);
  }

  private getApiCurrencyCode(plan: any, fallbackCode: string): string {
    const value = plan?.currencySymbol || fallbackCode;
    const normalized = String(value || fallbackCode).toUpperCase();
    if (normalized === '₹') return 'INR';
    if (normalized === '$') {
      return this.getLoggedInUserCountryName().trim().toLowerCase() === 'canada' ? 'CAD' : 'USD';
    }
    return normalized;
  }

  plans = [
    {
      id: 'solo',
      title: 'SOLO',
      price: {
        default: {
          original: null,
          current: 2.99,
          yearlyPrice: 29,
          currency: 'USD'
        },
        india: {
          original: null,
          current: 50,
          yearlyPrice: 480,
          currency: 'INR'
        }
      },
      period: '/ month',
      features: ['1 QR V Card', 'Unlimited QR scans', 'Contact sharing', 'Lead classification', 'Personal contact book', 'Export to CRM'],
      btnText: 'Get Started'
    },
    {
      id: 'team',
      title: 'TEAM',

      period: '/month',
      subtext: 'Default: 5 Users (5 - 10 - 25 - 50 - 100)',
      features: ['Everything in Solo', 'Up to 5 user QR V Cards', 'Centralized contact book', 'Role-based management', 'Lead ownership', 'Performance tracking'],
      btnText: 'Get Started'
    },
    {
      id: 'enterprise',
      title: 'ENTERPRISE',
      price: {
        default: {
          original: null,
          current: null,
          yearlyPrice: null,
          currency: 'USD'
        },
        india: {
          original: null,
          current: null,
          yearlyPrice: null,
          currency: 'INR'
        }
      },
      subtext: '',
      features: ['Everything in Team, plus enterprise-grade power.', 'CRM integration', 'API access', 'Custom workflows', 'Advanced permissions'],
      btnText: 'Contact Sales'
    }
  ];

  plans2 = [
    {
      id: 'Smart Site Lite', // Sequential ID
      name: 'Smart Site Lite',

      price: {
        default: {
          original: 39,
          current: 29,
          yearlyPrice: 299,
          currency: 'USD'
        },
        india: {
          original: 1750,
          current: 1395,
          yearlyPrice: 13950,
          currency: 'INR'
        }
      },
      savings: 'Save 2 months free',
      features: [
        'V Card Team — 5 Users',
        'QR-powered digital catalog',
        'Up to 25 QR-powered items',
        'Landing page builder',
        'Media support (images & videos)',
        'Analytics dashboard'
      ]
    },
    {
      id: 'Standard', // Sequential ID
      name: 'Standard',

      price: {
        default: {
          original: 49,
          current: 39,
          yearlyPrice: 399,
          currency: 'USD'
        },
        india: {
          original: 2800,
          current: 2250,
          yearlyPrice: 22500,
          currency: 'INR'
        }
      },

      savings: 'Save 2 months free',
      features: [
        'Everything in Lite, plus:',
        'Up to 100 QR-powered items',
        'Campaign creation & media publishing',
        'Secure validation & redemption',
        'Real-time performance dashboard',
        'Connect to 1 store',
        '+ Add more stores anytime'
      ]
    },
    {
      id: 'Standard Plus',
      name: 'Standard Plus',

      price: {
        default: {
          original: 79,
          current: 59,
          yealyPrice: 599,
          currency: 'USD'
        },
        india: {
          original: 4500,
          current: 3500,
          yearlyPrice: 35000,
          currency: 'INR'
        }
      },
      savings: 'Save 2 months',
      features: ['Everything in Standard', 'Up to 300 QR-powered items', 'Connect up to 5 stores']
    },
    {
      id: 'Standard Omni',
      name: 'Standard Omni',

      price: {
        default: {
          original: 129,
          current: 99,
          yearlyPrice: 999,
          currency: 'USD'
        },
        india: {
          original: 6500,
          current: 4999,
          yearlyPrice: 49990,
          currency: 'INR'
        }
      },
      savings: 'Save 2 months',
      features: ['Everything in Standard Plus', 'Up to 500 QR-powered items', 'Shopify / WooCommerce integration', 'Connect up to 10 stores']
    }
  ];
  setActive(id: string) {
    this.activePlanId = id;
    //console.log('Active Plan ID:', this.activePlanId);
  }

  onClickSubscription(plan: any) {
    var model = Object.assign({});
    model.subscriptionPlan = plan;
    this.userService.subscribe(model).pipe(first()).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.dialogRef.close();
          this.storageService.loginSubject.next(false);
          this.userInfoService.setLoginFlag(false);
          this.userInfoService.setToken();
          this.storageService.removeValue(StorageKey.authToken);
          this.storageService.removeValue(StorageKey.currentUser);
          this.storageService.removeValue(StorageKey.menuRights);
          sessionStorage.removeItem('currentUser');
          this.router.navigate(['/']);
        }
      }
    });
  }

  onClickPlanAction(plan: any) {
    if (String(plan?.title || '').toUpperCase() === 'ENTERPRISE') {
      this.priceModalRef = this.dialog.open(this.contactModal, {
        disableClose: true,
        width: '360px'
      });
      return;
    }

    this.selectedPlanForSubscription = plan;
    const priceModel = this.detectedCountry === 'IN' ? plan?.price?.india : plan?.price?.default;
    this.selectedMonthlyAmount = Number(priceModel?.current ?? 0);
    this.selectedAnnualAmount = Number(priceModel?.yearlyPrice ?? 0);
    this.selectedBilling = 'monthly';
    this.priceModalRef = this.dialog.open(this.planPriceModal, {
      disableClose: true,
      width: '420px'
    });
  }

  private openPendingSubscriptionPlan() {
    const pendingPlanId = localStorage.getItem('pendingSubscriptionPlanId');
    if (!pendingPlanId) return;

    const selectedPlan = this.findPlanFromPendingSelection(pendingPlanId);
    localStorage.removeItem('pendingSubscriptionPlanId');
    if (selectedPlan) {
      this.onClickPlanAction(selectedPlan);
      return;
    }

    this.alertService.showError('Plan not available in your region');
  }

  private findPlanFromPendingSelection(pendingPlanId: string): any {
    const allPlans = [
      this.soloPlan,
      ...this.teamPlans,
      ...this.smartSitePlans
    ];

    return allPlans.find((plan: any) => String(plan?.id || '') === String(pendingPlanId)) || null;
  }

  get selectedPlanFeatures(): string[] {
    return Array.isArray(this.selectedPlanForSubscription?.features) ? this.selectedPlanForSubscription.features : [];
  }

  get yearlySavingsPercent(): number {
    if (!this.selectedMonthlyAmount || !this.selectedAnnualAmount) return 0;
    const yearlyByMonth = this.selectedMonthlyAmount * 12;
    if (yearlyByMonth <= 0 || yearlyByMonth <= this.selectedAnnualAmount) return 0;
    return Math.round(((yearlyByMonth - this.selectedAnnualAmount) / yearlyByMonth) * 100);
  }

  get isSelectedPlanFreeTrial(): boolean {
    return this.isFreeAmount(this.selectedMonthlyAmount);
  }

  closePriceModal() {
    if (this.priceModalRef) {
      this.priceModalRef.close();
      this.priceModalRef = null;
    }
  }

  async onConfirmSubscription() {
    localStorage.removeItem('pendingSubscriptionPlanId');
    if (!this.selectedPlanForSubscription?.id || this.isProcessingSubscription) return;

    const amount = this.selectedBilling === 'annual' ? this.selectedAnnualAmount : this.selectedMonthlyAmount;
    const currency = this.getSelectedPlanCurrency();

    if (!Number.isFinite(amount)) {
      this.alertService.showError('Selected plan amount is invalid');
      return;
    }

    this.isProcessingSubscription = true;

    try {
      const planId = String(this.selectedPlanForSubscription.id);
      const currentUser = this.userInfoService.getUserInfo() || {};
      const userId = String(currentUser?.id || currentUser?._id || '');

      if (amount <= 0) {
        const subscribeResponse = await this.subscribeToFreePlan(planId);
        await this.afterSubscriptionPaymentVerified(subscribeResponse, userId, currentUser);
        return;
      }

      if (this.shouldUseRazorpay()) {
        const verifyResponse = await this.completeRazorpaySubscriptionPayment(amount, currency, planId, userId);
        await this.afterSubscriptionPaymentVerified(verifyResponse, userId, currentUser);
        return;
      }

      await this.startStripeSubscriptionPayment(amount, currency, planId, userId, currentUser);
    } catch (error: any) {
      this.alertService.showError(error?.message || 'Unable to complete subscription payment');
    } finally {
      this.isProcessingSubscription = false;
    }
  }

  private subscribeToFreePlan(planId: string): Promise<any> {
    return firstValueFrom(
      this.subscriptionService.subscribeToPlan(planId, this.buildSubscribePlanPayload()),
    );
  }

  private buildSubscribePlanPayload(): SubscribePlanRequest {
    const plan = this.selectedPlanForSubscription || {};
    return {
      duration: this.getSelectedPlanDuration(),
      vcardCount: Number(plan.vcardCount || 0),
      storeCount: Number(plan.storeCount || 0),
      featuredProductCount: Number(plan.featuredProductCount || 0),
    };
  }

  private getSelectedPlanDuration(): number {
    const planDuration = Number(this.selectedPlanForSubscription?.duration || 0);
    if (planDuration > 0) return this.selectedBilling === 'annual' ? planDuration * 12 : planDuration;
    return this.selectedBilling === 'annual' ? 12 : 1;
  }

  private shouldUseRazorpay(): boolean {
    return this.isIndiaCountry(this.getLoggedInUserCountryName());
  }

  private isIndiaCountry(country: string): boolean {
    const value = String(country || '').trim().toLowerCase();
    return value === 'india' || value === 'in';
  }

  private async completeRazorpaySubscriptionPayment(
    amount: number,
    currency: string,
    planId: string,
    userId: string,
  ): Promise<any> {
    const isRazorpayLoaded = await this.loadRazorpayScript();
    if (!isRazorpayLoaded) {
      throw new Error('Unable to load Razorpay checkout');
    }

    const orderResponse = await firstValueFrom(
      this.subscriptionService.createRazorpayOrder({
        amount,
        currency,
        receipt: `subscription_${Date.now()}`,
        details: {
          id: planId,
          type: 'subscription',
          duration: this.selectedBilling,
          userId,
        },
      }),
    );

    if (!orderResponse?.success || !orderResponse?.data?.orderId) {
      throw new Error(orderResponse?.message || 'Unable to create Razorpay order');
    }

    const razorpayResult = await this.openRazorpayCheckout(orderResponse.data);
    return firstValueFrom(
      this.subscriptionService.verifyRazorpayPayment({
        razorpayOrderId: razorpayResult.razorpay_order_id,
        razorpayPaymentId: razorpayResult.razorpay_payment_id,
        razorpaySignature: razorpayResult.razorpay_signature,
      }),
    );
  }

  private async startStripeSubscriptionPayment(
    amount: number,
    currency: string,
    planId: string,
    userId: string,
    currentUser?: any,
  ): Promise<void> {
    const intentResponse = await firstValueFrom(
      this.subscriptionService.createStripePaymentIntent({
          amount,
          currency,
          receipt: `subscription_${Date.now()}`,
          details: {
            id: planId,
            type: 'subscription',
            duration: this.selectedBilling,
            userId,
            planName: this.selectedPlanForSubscription?.name || this.selectedPlanForSubscription?.title || 'Subscription',
          },
        }),
      );

    if (
      !intentResponse?.success ||
      !intentResponse?.data?.publishableKey ||
      !intentResponse?.data?.clientSecret ||
      !intentResponse?.data?.paymentIntentId
    ) {
      throw new Error(intentResponse?.message || 'Unable to create Stripe payment');
    }

    const isStripeLoaded = await this.loadStripeScript();
    if (!isStripeLoaded || !window.Stripe) {
      throw new Error('Unable to load Stripe payment form');
    }

    this.stripe = window.Stripe(intentResponse.data.publishableKey);
    this.stripeElements = this.stripe.elements({
      clientSecret: intentResponse.data.clientSecret,
      appearance: {
        theme: 'stripe',
      },
    });
    this.stripePaymentIntentId = intentResponse.data.paymentIntentId;
    this.pendingStripeContext = { userId, currentUser: currentUser || {} };
    this.stripeErrorMessage = '';
    this.closePriceModal();
    this.stripeModalRef = this.dialog.open(this.stripePaymentModal, {
      disableClose: true,
      width: '480px',
    });
    setTimeout(() => this.mountStripePaymentElement());
  }

  async onConfirmStripePayment(): Promise<void> {
    if (!this.stripe || !this.stripeElements || !this.stripePaymentIntentId || this.isProcessingSubscription) return;

    this.isProcessingSubscription = true;
    this.stripeErrorMessage = '';
    try {
      const result = await this.stripe.confirmPayment({
        elements: this.stripeElements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (result?.error) {
        throw new Error(result.error.message || 'Stripe payment failed');
      }

      const paymentIntentId = result?.paymentIntent?.id || this.stripePaymentIntentId;
      const verifyResponse = await firstValueFrom(
        this.subscriptionService.verifyStripePayment({ paymentIntentId }),
      );
      const context = this.pendingStripeContext || { userId: '', currentUser: {} };
      await this.afterSubscriptionPaymentVerified(verifyResponse, context.userId, context.currentUser);
      this.closeStripePaymentModal();
    } catch (error: any) {
      this.stripeErrorMessage = error?.message || 'Unable to complete Stripe payment';
      this.alertService.showError(this.stripeErrorMessage);
    } finally {
      this.isProcessingSubscription = false;
    }
  }

  closeStripePaymentModal(): void {
    if (this.stripePaymentElement) {
      try {
        this.stripePaymentElement.unmount();
      } catch (e) {
        console.warn('Failed to unmount Stripe payment element', e);
      }
    }
    this.stripePaymentElement = null;
    this.stripeElements = null;
    this.stripe = null;
    this.stripePaymentIntentId = '';
    this.pendingStripeContext = null;
    this.stripeErrorMessage = '';
    if (this.stripeModalRef) {
      this.stripeModalRef.close();
      this.stripeModalRef = null;
    }
  }

  private mountStripePaymentElement(): void {
    const container = document.getElementById('stripe-payment-element');
    if (!container || !this.stripeElements) return;

    container.innerHTML = '';
    this.stripePaymentElement = this.stripeElements.create('payment', {
      layout: {
        type: 'accordion',
        defaultCollapsed: false,
      },
      defaultValues: {
        billingDetails: {
          name: this.getCurrentUserFullName(),
          email: this.pendingStripeContext?.currentUser?.email || '',
          phone: this.pendingStripeContext?.currentUser?.telephone || this.pendingStripeContext?.currentUser?.primaryContact || '',
        },
      },
    });
    this.stripePaymentElement.mount('#stripe-payment-element');
  }

  private getCurrentUserFullName(): string {
    const currentUser = this.pendingStripeContext?.currentUser || this.userInfoService.getUserInfo() || {};
    return `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();
  }

  private loadStripeScript(): Promise<boolean> {
    if (window.Stripe) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const existingScript = document.querySelector(
        'script[src="https://js.stripe.com/v3/"]',
      ) as HTMLScriptElement | null;

      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(true), { once: true });
        existingScript.addEventListener('error', () => resolve(false), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  private async afterSubscriptionPaymentVerified(verifyResponse: any, userId: string, currentUser: any): Promise<void> {
    const subscriptionResponse = this.normalizeSubscriptionActivationResponse(verifyResponse);

    if (!subscriptionResponse?.success || !subscriptionResponse?.data?.verified) {
      throw new Error(verifyResponse?.message || 'Payment verification failed');
    }

    if (!subscriptionResponse?.data?.subscription?.expiresAt) {
      throw new Error('Subscription activation failed');
    }

    //check user existing or not if not then generate in the vcard application,
    // As per discussion with Paul he metioned all plan have vcard involvment so
    // without condition it should apply on all plance to generate vcard user
    // 25/06/2026  base on discussion with Paul on 24/06/2026

    //checking user created or not
    await new Promise(resolve => setTimeout(resolve, 10000));

    this.vcardCompService.checkVCardUser(userId).subscribe({
        next: (res: any) => {
          if(!res.success) {

            const payload = {
              "email":  currentUser.email,
              "password": "Abc@12345",
              "firstName": currentUser.firstName,
              "lastName": currentUser.lastName,
              "mobile": `+` + currentUser.countryCode,
              "address": currentUser.city + "," + currentUser.state + "," + currentUser.country,
              "ownerId": userId,
              "maxUsers": subscriptionResponse.data?.subscription?.vcardCount
            };

            //first registratio in alyro then approve full registration part
            this.vcardCompService.addVcardRegistration(payload).subscribe({
              next: (resCr:any) => {
                if(resCr.success) {
                  //success
                  this.alertService.showSuccess(resCr.message);

                   const vcardToken = localStorage.getItem('vcardToken');

                    if (!vcardToken) {
                      var vcardLogin: any = {
                        "email": currentUser.email.trim(),
                      }
                      this.vcardUserService.generateToken(vcardLogin).subscribe((restok:any) => {
                        if(restok.token) {
                          sessionStorage.setItem('vcardUser', JSON.stringify({ token: restok.token, userInfo: restok.user }));
                          localStorage.setItem('vcardToken', restok.token);
                            localStorage.setItem('vcardUser', JSON.stringify(restok.user));
                        }
                         this.router.navigate(['admin/vcard-contact']);
                      });
                         
                   
                    }
                     
                } else {
                  this.alertService.showError(`Fail to add new user in Alyro Application`);
                }
              },
              error: (error) => {
                 console.log(error);
                 this.alertService.showError(error?.message);
              }
                
            });
          } else {
             //update company details   
             const id = res.data.id;     
             const payload = {maxUsers: subscriptionResponse.data?.subscription?.vcardCount}     
            this.vcardCompService.update(id,payload).subscribe((res:any)=> {
                if(res.success){
                  this.alertService.showSuccess(`Success to user date in Alyro Application`);
                } else {
                  this.alertService.showError(`Fail to update user data in Alyro Application`);
                }                
             });
          }
        },
        error:(err)=>{

          console.log(err);
          this.alertService.showError(err?.message);
        }
      });

      this.handleSubscribeSuccess(subscriptionResponse);
      this.closePriceModal();
      this.dialogRef.close(true);
  }

  private normalizeSubscriptionActivationResponse(response: any): any {
    const data = response?.data || response;
    const subscription = data?.subscription || data;

    return {
      ...response,
      success: response?.success !== false,
      data: {
        ...data,
        verified: data?.verified !== false,
        subscription,
      },
    };
  }

  private handleSubscribeSuccess(response: any): void {
  const subscriptionData = response?.data?.subscription || response?.data;
  const expiresAtRaw = subscriptionData?.expiresAt;
  const expiresAtMs = expiresAtRaw ? new Date(expiresAtRaw).getTime() : NaN;

  if (!Number.isNaN(expiresAtMs) && expiresAtMs > Date.now()) {
    const rawCurrentUser = this.storageService.getValue(StorageKey.currentUser);
    if (rawCurrentUser) {
      try {
        const parsedUser = JSON.parse(rawCurrentUser);
        parsedUser.planExpired = false;
        this.storageService.setValue(StorageKey.currentUser, JSON.stringify(parsedUser));
        this.userInfoService.setUserInfo(parsedUser);
      } catch (e) {
        console.warn('Failed to update current user expiry state', e);
      }
    }

    this.roleManagementService.access().pipe(first()).subscribe({
      next: (menuResponse: any) => {
        const menuRights = menuResponse?.success && Array.isArray(menuResponse?.data) ? menuResponse.data : [];
        this.storageService.setValue(StorageKey.menuRights, JSON.stringify(menuRights));
        this.storageService.menuListSubject.next(true);
        if (menuResponse?.success) {
          this.router.navigate(['/admin/dashboard']).then(() => {
            window.location.reload();
          });
        }
      },
      error: () => {
        this.storageService.setValue(StorageKey.menuRights, JSON.stringify([]));
        this.storageService.menuListSubject.next(true);
      }
    });
  }
}
//   private handleSubscribeSuccess(response: any): void {
//     const subscriptionData = response?.data?.subscription || response?.data;
//     const expiresAtRaw = subscriptionData?.expiresAt;
//     const expiresAtMs = expiresAtRaw ? new Date(expiresAtRaw).getTime() : NaN;

//     if (!Number.isNaN(expiresAtMs) && expiresAtMs > Date.now()) {
//       const rawCurrentUser = this.storageService.getValue(StorageKey.currentUser);
//       if (rawCurrentUser) {
//         try {
//           const parsedUser = JSON.parse(rawCurrentUser);
//           parsedUser.planExpired = false;
//           this.storageService.setValue(StorageKey.currentUser, JSON.stringify(parsedUser));
//           this.userInfoService.setUserInfo(parsedUser);
//         } catch (e) {
//           console.warn('Failed to update current user expiry state', e);
//         }
//       }

//       // this.roleManagementService.access().pipe(first()).subscribe({
//       //   next: (menuResponse: any) => {
//       //     const menuRights = menuResponse?.success && Array.isArray(menuResponse?.data) ? menuResponse.data : [];
//       //     this.storageService.setValue(StorageKey.menuRights, JSON.stringify(menuRights));
//       //     this.storageService.menuListSubject.next(true);
//       //     if (menuResponse?.success) {
//       //       this.router.navigate(['/admin/products']).then(() => {
//       //         window.location.reload();
//       //       });
//       //     }
//       //   },
//       //   error: () => {
//       //     this.storageService.setValue(StorageKey.menuRights, JSON.stringify([]));
//       //     this.storageService.menuListSubject.next(true);
//       //   }
//       // });
//       this.roleManagementService.access().pipe(first()).subscribe({
//   next: (menuResponse: any) => {
//     const menuRights = menuResponse?.success && Array.isArray(menuResponse?.data) ? menuResponse.data : [];
//     this.storageService.setValue(StorageKey.menuRights, JSON.stringify(menuRights));
//     this.storageService.menuListSubject.next(true);
//     if (menuResponse?.success) {
//       this.router.navigate(['/admin/products']).then(() => { // <--- Redirecting to items/products here
//         window.location.reload();
//       });
//     }
//   },
//   // ...
// });
//     }
//   }

  private getSelectedPlanCurrency(): string {
    const priceModel = this.detectedCountry === 'IN'
      ? this.selectedPlanForSubscription?.price?.india
      : this.selectedPlanForSubscription?.price?.default;

    return this.normalizeCurrencyCode(priceModel?.currencyCode || priceModel?.currency || 'INR');
  }

  private normalizeCurrencyCode(currency: string): string {
    if (currency === '₹') return 'INR';
    if (currency === '$') return 'USD';
    return String(currency || 'INR').toUpperCase();
  }

  private normalizeCurrencySymbol(currency: string | null): string | null {
    if (!currency) return null;
    const value = String(currency).toUpperCase();
    if (value === 'INR') return '₹';
    if (value === 'USD') return '$';
    return currency;
  }

  private loadRazorpayScript(): Promise<boolean> {
    if (window.Razorpay) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const existingScript = document.querySelector(
        'script[src="https://checkout.razorpay.com/v1/checkout.js"]',
      ) as HTMLScriptElement | null;

      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(true), { once: true });
        existingScript.addEventListener('error', () => resolve(false), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  private openRazorpayCheckout(orderData: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!window.Razorpay) {
        reject(new Error('Razorpay checkout is unavailable'));
        return;
      }

      const currentUser = this.userInfoService.getUserInfo() || {};
      const fullName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();
      let paymentHandled = false;

      const razorpay = new window.Razorpay({
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Scanfluence',
        description: `${this.selectedPlanForSubscription?.name || this.selectedPlanForSubscription?.title || 'Subscription'} (${this.selectedBilling})`,
        order_id: orderData.orderId,
        prefill: {
          name: fullName || currentUser?.company || '',
          email: currentUser?.email || '',
          contact: currentUser?.telephone || currentUser?.primaryContact || currentUser?.waNumber || '',
        },
        notes: {
          planId: String(this.selectedPlanForSubscription?.id || ''),
          billingCycle: this.selectedBilling,
        },
        theme: {
          color: '#1976d2',
        },
        modal: {
          ondismiss: () => {
            if (!paymentHandled) {
              reject(new Error('Payment was cancelled'));
            }
          },
        },
        handler: (response: any) => {
          paymentHandled = true;
          resolve(response);
        },
      });

      razorpay.on('payment.failed', (response: any) => {
        if (!paymentHandled) {
          reject(
            new Error(
              response?.error?.description ||
              response?.error?.reason ||
              'Payment failed',
            ),
          );
        }
      });

      razorpay.open();
    });
  }

  getSubscriptionPlans(): Promise<void> {
    return new Promise((resolve) => {
      this.subscriptionService.list(this.getLoggedInUserCountryName()).subscribe((response: any) => {
        if (!response?.success || !Array.isArray(response?.data)) {
          resolve();
          return;
        }

        const approvedPlans = response.data.filter((x: any) => String(x?.status || '').toUpperCase() === 'APPROVED');

        const vcardPlans = approvedPlans.filter((x: any) => x?.type === 'vcard');
        const smartSitePlans = approvedPlans.filter((x: any) => x?.type === 'smart_site');

        const mappedVcardPlans = vcardPlans.map((plan: any) => ({
          id: plan.id,
          title: String(plan.name || '').toUpperCase(),
          name: plan.name,
          vcardCount: Number(plan.vcardCount || 0),
          price: {
            default: {
              original: null,
              current: plan.price ?? 0,
              yearlyPrice: plan.annualPrice ?? 0,
              currency: this.getApiCurrencySymbol(plan) || '$',
              currencyCode: this.getApiCurrencyCode(plan, 'USD')
            },
            india: {
              original: null,
              current: plan.price ?? 0,
              yearlyPrice: plan.annualPrice ?? 0,
              currency: this.getApiCurrencySymbol(plan) || '₹',
              currencyCode: this.getApiCurrencyCode(plan, 'INR')
            }
          },
          period: '/ month',
          duration: Number(plan.duration || 0),
          subtext: plan.description || '',
          features: Array.isArray(plan.inclusions) ? plan.inclusions : [],
          btnText: 'Get Started'
        }));

        // 1) solo plan: only one plan where vcardCount === 1
        this.soloPlan = mappedVcardPlans.find((x: any) => x.vcardCount === 1) || null;
        // 2) team plans: all plans where vcardCount > 1
        this.teamPlans = mappedVcardPlans.filter((x: any) => x.vcardCount > 1);
        this.userOptions = [...new Set(this.teamPlans.map((x: any) => Number(x.vcardCount)).filter((x: number) => x > 1))]
          .sort((a: number, b: number) => a - b);
        if (this.userOptions.length) {
          this.user = this.userOptions.includes(this.user) ? this.user : this.userOptions[0];
        }
        this.checkForTeam();

        this.smartSitePlans = smartSitePlans.map((plan: any) => ({
          id: plan.id,
          name: plan.name,
          description: plan.description || '',
          price: {
            default: {
              original: (Number(plan.price ?? 0) * 12),
              current: plan.price ?? 0,
              yearlyPrice: plan.annualPrice ?? 0,
              currency: this.getApiCurrencySymbol(plan) || '$',
              currencyCode: this.getApiCurrencyCode(plan, 'USD')
            },
            india: {
              original: (Number(plan.price ?? 0) * 12),
              current: plan.price ?? 0,
              yearlyPrice: plan.annualPrice ?? 0,
              currency: this.getApiCurrencySymbol(plan) || '₹',
              currencyCode: this.getApiCurrencyCode(plan, 'INR')
            }
          },
          duration: Number(plan.duration || 0),
          vcardCount: Number(plan.vcardCount || 0),
          storeCount: Number(plan.storeCount || 0),
          featuredProductCount: Number(plan.featuredProductCount || 0),
          features: Array.isArray(plan.inclusions) ? plan.inclusions : []
        }));

        if (this.smartSitePlans.length) this.plans2 = this.smartSitePlans;
        resolve();
      }, () => resolve());
    });
  }

  private getLoggedInUserCountryName(): string {
    const currentUser = this.getCurrentUserFromSession() || this.userInfoService.getUserInfo() || {};
    return String(
      currentUser?.countryName ||
      currentUser?.country?.name ||
      currentUser?.country ||
      currentUser?.countryId ||
      ''
    ).trim();
  }

  private getCurrentUserFromSession(): any {
    const rawCurrentUser = sessionStorage.getItem('currentUser');
    if (!rawCurrentUser) return null;

    try {
      const parsed = JSON.parse(rawCurrentUser);
      return parsed?.userInfo || parsed;
    } catch (e) {
      console.warn('Failed to parse session current user', e);
      return null;
    }
  }

  private async tryIpGeolocation(): Promise<string | null> {
    try {
      const resp = await fetch('https://ipapi.co/json/');
      if (!resp.ok) return null;
      const data = await resp.json();
      if (data && data.country_code) {
        return data.country_code.toUpperCase();
      }
      return null;
    } catch (e) {
      console.warn('ip geolocation failed', e);
      return null;
    }
  }

}
